import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = {
  primary: '#2b3380',
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336'
};

function WhatsAppSettings({ user, onLogout }) {
  const [settings, setSettings] = useState({
    phone_number_id: '',
    access_token: '',
    business_account_id: '',
    webhook_verify_token: '',
    app_secret: '',
    api_version: 'v21.0'
  });
  
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showGuide, setShowGuide] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testTemplate, setTestTemplate] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.configured) {
        setSettings(response.data);
        setConfigured(true);
        fetchTemplates();
        fetchMessages();
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/messages?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('✅ WhatsApp settings saved successfully!');
      setConfigured(true);
      fetchTemplates();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTestMessage = async () => {
    if (!testPhone || !testTemplate) {
      alert('Please enter phone number and select template');
      return;
    }

    setTestLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/send-template`, {
        to: testPhone,
        template_name: testTemplate,
        language: 'en_US'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('✅ Test message sent successfully!');
      fetchMessages();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary }}>
            📱 WhatsApp Business API
          </h1>
          <p className="text-gray-600">
            Configure Official WhatsApp Business Cloud API for customer & employee communication
          </p>
        </div>

        {/* Setup Guide Button */}
        <div className="mb-4">
          <button
            onClick={() => setShowGuide(!showGuide)}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            {showGuide ? '📖 Hide' : '📖 Show'} Setup Guide
          </button>
        </div>

        {/* Setup Guide */}
        {showGuide && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>
              🚀 WhatsApp Business API Setup Guide
            </h2>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-bold mb-2">Step 1: Create Meta Developer Account</h3>
                <p className="text-sm text-gray-600 mb-2">
                  1. Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">developers.facebook.com</a>
                  <br/>2. Click "Get Started" and create a developer account
                  <br/>3. Verify your email and complete profile
                </p>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-bold mb-2">Step 2: Create Business App</h3>
                <p className="text-sm text-gray-600 mb-2">
                  1. Click "My Apps" → "Create App"
                  <br/>2. Select "Business" app type
                  <br/>3. Name your app (e.g., "Zobox CRM")
                  <br/>4. Add "WhatsApp" product to your app
                </p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-bold mb-2">Step 3: Get API Credentials</h3>
                <p className="text-sm text-gray-600 mb-2">
                  1. In WhatsApp settings, find:
                  <br/>&nbsp;&nbsp;&nbsp;• <strong>Phone Number ID</strong> (starts with numbers)
                  <br/>&nbsp;&nbsp;&nbsp;• <strong>Access Token</strong> (click "Create Token")
                  <br/>&nbsp;&nbsp;&nbsp;• <strong>WhatsApp Business Account ID</strong> (WABA ID)
                  <br/>2. Copy these values and paste below
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="font-bold mb-2">Step 4: Verify Phone Number</h3>
                <p className="text-sm text-gray-600 mb-2">
                  1. Add a phone number to your WhatsApp Business Account
                  <br/>2. Verify via SMS (use a real business number)
                  <br/>3. Note: Test number allows 5 recipients only
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="font-bold mb-2">Step 5: Create Message Templates</h3>
                <p className="text-sm text-gray-600 mb-2">
                  1. Go to WhatsApp Manager → Message Templates
                  <br/>2. Create templates (e.g., "leave_approved", "order_confirmation")
                  <br/>3. Wait for Meta approval (1-2 days)
                  <br/>4. Templates must be approved before sending
                </p>
              </div>
            </div>

            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 Important:</strong> WhatsApp charges per conversation. First 1000 conversations/month are free. 
                Template messages start new conversations. Text replies within 24h are free.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>
                ⚙️ API Configuration
              </h2>
              
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number ID *
                  </label>
                  <input
                    type="text"
                    value={settings.phone_number_id}
                    onChange={(e) => setSettings({...settings, phone_number_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123456789012345"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">From WhatsApp → API Setup → Phone Number ID</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Token *
                  </label>
                  <input
                    type="password"
                    value={settings.access_token}
                    onChange={(e) => setSettings({...settings, access_token: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="EAAxxxxxxxxxx..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Create permanent token from your app settings</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Account ID (WABA ID) *
                  </label>
                  <input
                    type="text"
                    value={settings.business_account_id}
                    onChange={(e) => setSettings({...settings, business_account_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123456789012345"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">WhatsApp Business Account ID</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook Verify Token *
                  </label>
                  <input
                    type="text"
                    value={settings.webhook_verify_token}
                    onChange={(e) => setSettings({...settings, webhook_verify_token: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="my_secret_verify_token_123"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Create your own secure token for webhook verification</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    App Secret (Optional)
                  </label>
                  <input
                    type="password"
                    value={settings.app_secret}
                    onChange={(e) => setSettings({...settings, app_secret: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional for webhook signature verification"
                  />
                  <p className="text-xs text-gray-500 mt-1">From App Settings → Basic → App Secret</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Version
                  </label>
                  <input
                    type="text"
                    value={settings.api_version}
                    onChange={(e) => setSettings({...settings, api_version: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="v21.0"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  {loading ? '⏳ Saving...' : '💾 Save Configuration'}
                </button>
              </form>
            </div>

            {/* Test Message */}
            {configured && (
              <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <h2 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>
                  🧪 Test Message
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number (with country code)
                    </label>
                    <input
                      type="text"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="919876543210"
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: Country code + number (no + or spaces)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template
                    </label>
                    <select
                      value={testTemplate}
                      onChange={(e) => setTestTemplate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select template</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.name}>
                          {template.name} ({template.status})
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={handleTestMessage}
                    disabled={testLoading || !testPhone || !testTemplate}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {testLoading ? '📤 Sending...' : '📤 Send Test Message'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-3" style={{ color: COLORS.primary }}>
                📊 Status
              </h3>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Configuration:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {configured ? '✅ Configured' : '❌ Not Configured'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Templates:</span>
                  <span className="text-sm font-medium">{templates.length} loaded</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Messages:</span>
                  <span className="text-sm font-medium">{messages.length} recent</span>
                </div>
              </div>
            </div>

            {/* Message Templates */}
            {configured && templates.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-3" style={{ color: COLORS.primary }}>
                  📝 Templates
                </h3>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {templates.slice(0, 10).map((template) => (
                    <div key={template.id} className="border-b pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium">{template.name}</p>
                          <p className="text-xs text-gray-500">{template.language}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          template.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                          template.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {template.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Messages */}
            {configured && messages.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-3" style={{ color: COLORS.primary }}>
                  💬 Recent Messages
                </h3>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {messages.slice(0, 5).map((msg, idx) => (
                    <div key={idx} className="border-b pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium">{msg.type === 'sent_template' ? '📤 Sent' : '📥 Received'}</p>
                          <p className="text-xs text-gray-500">{msg.to || msg.from}</p>
                          <p className="text-xs text-gray-500">{new Date(msg.logged_at).toLocaleString()}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          msg.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                          msg.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          msg.status === 'read' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {msg.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppSettings;
